/**
 * 닫힌 오버레이는 트리에 두지 않는다 — 트랙 B-3 (docs/DESIGN-perf-track.md §3).
 *
 * MUI Dialog·Menu·Popover·Drawer·Modal 은 open=false 여도 컴포넌트 트리(Modal·Portal·Transition·Paper 스타일 계산)가 살아 있어
 * 부모가 다시 그려질 때마다 함께 돈다(실측: 커밋마다 Modal 0.1~0.25초, Popover 0.3초, 실행 화면에서 배치마다).
 * 여기서는 open 이 아닐 때 아무것도 만들지 않는다. 대가는 닫힐 때의 퇴장 애니메이션(195ms 페이드) 생략.
 * 앱은 이 모듈에서만 오버레이를 가져온다(overlayMount 검출기) — 선언 한 곳.
 *
 * Snackbar 는 다른 규칙 하나를 진다 — **알림은 바깥 클릭으로 닫지 않는다**(아래 참조). MUI Snackbar 는 닫혀 퇴장이 끝나면
 * 스스로 null 을 돌려주므로 미마운트 규칙은 따로 두지 않는다.
 */
import {
  Dialog as MuiDialog,
  Menu as MuiMenu,
  Popover as MuiPopover,
  Drawer as MuiDrawer,
  Modal as MuiModal,
  Snackbar as MuiSnackbar,
  type DialogProps,
  type MenuProps,
  type PopoverProps,
  type DrawerProps,
  type ModalProps,
  type SnackbarProps,
} from '@mui/material';

export type { DialogProps, MenuProps, PopoverProps, DrawerProps, ModalProps, SnackbarProps };

export function Dialog(props: DialogProps) {
  return props.open ? <MuiDialog {...props} /> : null;
}

export function Menu(props: MenuProps) {
  return props.open ? <MuiMenu {...props} /> : null;
}

export function Popover(props: PopoverProps) {
  return props.open ? <MuiPopover {...props} /> : null;
}

/** permanent 는 항상 있는 서랍이라 open 과 무관하게 그린다 */
export function Drawer(props: DrawerProps) {
  return props.variant === 'permanent' || props.open ? <MuiDrawer {...props} /> : null;
}

export function Modal(props: ModalProps) {
  return props.open ? <MuiModal {...props} /> : null;
}

/**
 * 알림은 **바깥 클릭으로 닫지 않는다** — 자동 숨김·닫기(X)·액션 버튼으로만.
 *
 * 바깥 클릭을 닫힘으로 치면, 알림이 떠 있는 동안 하는 다음 동작(연속 커밋·연속 저장)이 «자기 알림을 닫는 클릭» 이 된다 —
 * 같은 클릭이 새 알림을 올리고(React 핸들러) 바로 닫아(ClickAwayListener) 퇴장 전이 뒤에 사라지므로 [버전으로 기록] 같은
 * 액션을 누를 수 없다(2026-09-22 실측, modelicaVersionHistory 가 8회 중 7회 여기서 떨어졌다; #562). 버튼 없는 알림도 같은
 * 클릭에 지워지는 것은 같아, 앱의 모든 알림이 한 규칙을 쓴다(사용자 확정 09-22). MUI 문서도 `clickaway` 는 무시하라고 적는다.
 * Alert 의 닫기(X)는 Alert 자신의 onClose 라 이 규칙과 무관하다.
 */
export function Snackbar({ onClose, ...props }: SnackbarProps) {
  return (
    <MuiSnackbar
      {...props}
      onClose={onClose ? (event, reason) => { if (reason === 'clickaway') return; onClose(event, reason); } : undefined}
    />
  );
}
